const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const FULL_DEDUCTION = 100;

const plural = (count, unit) => `${count} ${unit}${count === 1 ? '' : 's'}`;

/**
 * Format a points value without trailing zeros: 60, 52.5, 33.33.
 *
 * @param {number|string} value
 * @returns {string}
 */
export function formatPoints(value) {
    const number = Number(value);

    return Number.isInteger(number) ? String(number) : String(Number(number.toFixed(2)));
}

/**
 * Format a points value to exactly two decimals, as the grader card and the review modal
 * state them: 60.00, 52.50, 33.33.
 *
 * @param {number|string} value
 * @returns {string}
 */
export function formatScore(value) {
    return Number(value).toFixed(2);
}

/**
 * The score the grader entered, for the score box. A grade with a late deduction stores
 * the effective score in `points`, and the box must show the raw score beside the card's
 * formula. `knownDeduction` covers grade objects that arrive without `late_deduction`,
 * such as a submission row: it applies only while their points still match it.
 *
 * @param {object|null} grade
 * @param {object|null} [knownDeduction]
 * @returns {number|null|undefined}
 */
export function scoreEntered(grade, knownDeduction = null) {
    const points = grade?.points;
    const deduction = grade?.late_deduction ?? knownDeduction;

    if (!deduction || points === null || points === undefined || points === '') {
        return points;
    }

    return Math.abs(Number(points) - Number(deduction.effective_points)) < 0.0001
        ? Number(deduction.raw_points)
        : points;
}

/**
 * How late the work was, measured from the due time as the calculation does. Truncated,
 * never rounded, so 24 hours 20 minutes never reads as "24 hours" beside an up-to-24-hours
 * tier. Under 48 hours it keeps the minutes, because tier bounds are in hours. Under a
 * minute it says so rather than claiming a whole minute.
 * "less than a minute", "1 minute", "6 hours 59 minutes", "2 days 3 hours".
 *
 * @param {string} dueAt
 * @param {string} submittedAt
 * @returns {string|null}
 */
export function latenessLabel(dueAt, submittedAt) {
    const late = lateMilliseconds(dueAt, submittedAt);

    if (late === null) {
        return null;
    }

    if (late < MINUTE) {
        return 'less than a minute';
    }

    if (late < HOUR) {
        return plural(Math.floor(late / MINUTE), 'minute');
    }

    if (late < 2 * DAY) {
        const hours = Math.floor(late / HOUR);
        const minutes = Math.floor((late - hours * HOUR) / MINUTE);

        return minutes > 0 ? `${plural(hours, 'hour')} ${plural(minutes, 'minute')}` : plural(hours, 'hour');
    }

    const days = Math.floor(late / DAY);
    const hours = Math.floor((late - days * DAY) / HOUR);

    return hours > 0 ? `${plural(days, 'day')} ${plural(hours, 'hour')}` : plural(days, 'day');
}

function lateMilliseconds(dueAt, submittedAt) {
    if (!dueAt || !submittedAt) {
        return null;
    }

    const late = new Date(submittedAt).getTime() - new Date(dueAt).getTime();

    return Number.isFinite(late) && late > 0 ? late : null;
}

/**
 * The percentage the policy itself calculated, which an instructor override replaces.
 *
 * @param {object} deduction
 * @returns {number}
 */
export function policyPercentage(deduction) {
    const rule = deduction?.matched_rule;

    if (!rule) {
        return FULL_DEDUCTION;
    }

    if (rule.days !== undefined) {
        const maximum = deduction.policy?.definition?.maximum_percentage;
        const total = Number(rule.days) * Number(rule.daily_percentage);

        return Math.min(total, maximum ?? FULL_DEDUCTION, FULL_DEDUCTION);
    }

    return Number(rule.percentage);
}

/**
 * The percentage to state beside the rule. The staff view carries the server's own
 * calculation; an applied student deduction is by definition the policy's percentage.
 *
 * @param {object} deduction
 * @returns {number}
 */
function ruleOutcomePercentage(deduction) {
    if (deduction?.policy_percentage !== undefined && deduction?.policy_percentage !== null) {
        return Number(deduction.policy_percentage);
    }

    if (deduction?.state === 'applied') {
        return Number(deduction.deduction_percentage);
    }

    return policyPercentage(deduction);
}

/**
 * The matched rule in the PRD's plain language, lowercase so it can follow a colon:
 * "up to 24 hours \u2192 10% deducted".
 *
 * @param {object} deduction
 * @returns {string}
 */
function ruleClause(deduction) {
    const rule = deduction?.matched_rule;
    const outcome = `\u2192 ${formatPoints(ruleOutcomePercentage(deduction))}% deducted`;

    if (!rule) {
        return `past the final tier ${outcome}`;
    }

    if (rule.days !== undefined) {
        const uncapped = Number(rule.days) * Number(rule.daily_percentage);
        const cap = ruleOutcomePercentage(deduction) < uncapped ? ' (maximum)' : '';

        return `${plural(Number(rule.days), 'day')} at ${formatPoints(rule.daily_percentage)}% per day ${outcome}${cap}`;
    }

    // Past the final tier under apply-last-tier, the last tier is the matched rule.
    const late = lateMilliseconds(deduction.due_at, deduction.submitted_at);
    const bound = late !== null && late > Number(rule.hours) * HOUR ? 'more than' : 'up to';

    return `${bound} ${plural(Number(rule.hours), 'hour')} ${outcome}`;
}

const capitalize = text => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * The matched rule as a sentence, for the grader card.
 *
 * @param {object} deduction
 * @returns {string}
 */
export function matchedRuleLabel(deduction) {
    return `${capitalize(ruleClause(deduction))}.`;
}

/**
 * The one-line explanation shown wherever a grade appears (FR-081, FR-085, FR-086).
 * A student view of a modification or waiver carries no rule and no reason, so it reads
 * as the outcome only. A staff view says what changed, in the grader card's words.
 *
 * @param {object} deduction
 * @returns {string}
 */
export function deductionExplanation(deduction) {
    const staff = isStaffView(deduction);

    if (deduction?.state === 'waived') {
        return staff
            ? `Deduction waived for this grade (policy calculated ${formatPoints(ruleOutcomePercentage(deduction))}%).`
            : 'Late penalty waived by your instructor.';
    }

    if (deduction?.state === 'modified') {
        return staff
            ? `Deduction changed from ${formatPoints(ruleOutcomePercentage(deduction))}% to ${formatPoints(deduction.deduction_percentage)}% for this grade.`
            : `Late penalty adjusted by your instructor to ${formatPoints(deduction.deduction_percentage)}% deducted.`;
    }

    const lateness = latenessLabel(deduction?.due_at, deduction?.submitted_at);
    const name = deduction?.policy_name ?? deduction?.policy?.name;
    const policy = name ? `Late Submission Policy (${name})` : 'Late Submission Policy';

    return `${lateness ? `Submitted ${lateness} late. ` : ''}${policy}: ${ruleClause(deduction)}.`;
}

/**
 * The explanation the instructor gradebook shows on the late pill and the cell's hover text,
 * prefixed so it names the policy it comes from. Student surfaces keep the unprefixed
 * `deductionExplanation`.
 *
 * @param {object} deduction
 * @returns {string}
 */
export function gradebookDeductionExplanation(deduction) {
    return `Late Submission Policy: ${deductionExplanation(deduction)}`;
}

/**
 * The short visible text for a grid cell, so the indicator is not colour or tooltip alone
 * (FR-084). U+2212 minus.
 *
 * @param {object} deduction
 * @returns {string}
 */
export function deductionMarker(deduction) {
    if (deduction?.state === 'waived') return 'Waived';
    if (deduction?.state === 'modified') return 'Modified';

    return `\u2212${formatPoints(deduction?.deduction_percentage)}%`;
}

/**
 * Whether the server sent the staff view. Only the staff view carries the raw score.
 *
 * @param {object} deduction
 * @returns {boolean}
 */
export function isStaffView(deduction) {
    return deduction?.raw_points !== undefined;
}

/**
 * The learner's notice before they submit, in the PRD's wording (FR-085). It states a
 * percentage, never points, because no score exists yet. `dueLabel` arrives formatted
 * because the institution timezone belongs to the caller.
 *
 * `summary`: "This activity was due Sep 28, 11:59 PM. If you submit now, it is 26 hours late."
 * `policy`: "Late Submission Policy (Institution Standard): up to 48 hours → 25% deducted."
 * `zero`: set only when the submission ends in zero points.
 *
 * @param {object|null} pending
 * @param {string} dueLabel
 * @returns {{summary: string, policy: string, zero: string|null}|null}
 */
export function pendingLateDeductionSentences(pending, dueLabel) {
    if (!pending) {
        return null;
    }

    const lateness = latenessLabel(pending.due_at, pending.as_of);
    const rule = ruleClause({
        matched_rule: pending.matched_rule,
        policy_percentage: pending.deduction_percentage,
        due_at: pending.due_at,
        submitted_at: pending.as_of,
    });
    const policy = pending.policy_name ? `Late Submission Policy (${pending.policy_name})` : 'Late Submission Policy';

    return {
        summary: `This activity was due ${dueLabel}. If you submit now, it is ${lateness ? `${lateness} late` : 'late'}.`,
        policy: `${policy}: ${rule}.`,
        zero: pending.ends_in_zero ? 'If you submit now you will receive 0 points for this activity.' : null,
    };
}
